import { ReadonlyMat4, vec2 } from 'gl-matrix';
import { Vertex } from './Vertex';
import { Edge } from '../Edge';

/**
 * {@link SliceMesh} provides static utility methods for mesh operations.
 */
export class SliceMesh {
    /**
     * Custom tessellation function that will somewhat efficiently tessellate convex polygons.\
     * This computes the centroid (average of all the points) that make up the polygon.\
     * Subtracts that point from all the points of the polygon.\
     * Calculates the orientation of that point relative to the center using {@link Math.atan2}.\
     * Sorts the points based on their rotation.\
     * And constructs an index buffer based on that ordering.
     * @param vertexCoordinates {@link Float32Array} containing all the vertex coordinates.
     * @param startIndex The index value that the very first point should have.\
     * As the mesh may be made up of multiple sections that are not connected to one another,\
     * but still want to save them into a single {@link VertexArrayObject} as those meshes should always be rendered together.\
     * So we {@link tesselate} them separately starting the second structure from where the first one left off.
     * @returns An {@link Array} of indices forming primitives of type {@link WebGL2RenderingContext.TRIANGLES}.
     */
    public static tesselate(
        vertexCoordinates: Float32Array,
        startIndex: number
    ): number[] {
        const numberOfPoints = vertexCoordinates.length / 2;
        if (numberOfPoints > 2) {
            const total = vec2.create();
            let i = 0;
            while (i < vertexCoordinates.length) {
                total[0] += vertexCoordinates[i++];
                total[1] += vertexCoordinates[i++];
            }
            const centroid = vec2.scale(
                vec2.create(),
                total,
                1 / numberOfPoints
            );

            const points: { orientation: number; index: number }[] = new Array<{
                orientation: number;
                index: number;
            }>(numberOfPoints);
            //calculate orientation of each index
            i = 0;
            let index = 0;
            while (i < vertexCoordinates.length) {
                const x = vertexCoordinates[i++] - centroid[0];
                const y = vertexCoordinates[i++] - centroid[1];
                //I tried optimized atan2 https://mazzo.li/posts/vectorized-atan2.html, seems javascript's default is equally fast. WASM might be faster
                points[index] = {
                    orientation: Math.atan2(y, x),
                    index: startIndex + index,
                };
                ++index;
            }
            points.sort(
                (
                    a: { orientation: number; index: number },
                    b: { orientation: number; index: number }
                ): number => {
                    return a.orientation - b.orientation;
                }
            );
            const indices = new Array<number>((numberOfPoints - 2) * 3);
            const last = points[numberOfPoints - 1];
            let currentIndex = 0;
            for (let a = 1, b = numberOfPoints - 1; a < b; ++a) {
                indices[currentIndex++] = points[a - 1].index;
                indices[currentIndex++] = points[a].index;
                indices[currentIndex++] = last.index;
            }

            return indices;
        } else if (numberOfPoints > 0) {
            return [0, numberOfPoints === 2 ? 1 : 0, 0];
        }
        return [];
    }

    /**
     * Method to get the intersection points for a single section of a mesh.
     * Section may consist of either 4 edges if it's just a single slice without any thickness.
     * Or 12 edges if it consists of multiple slices or has a thickness.
     * @param edges An {@link Array} of 4 or 12 {@link Edge Edges} that the section is made up of.
     * @param toProjection {@link ReadonlyMat4 TransformationMatrix} from PatientSpace to ProjectionSpace.\
     * All the {@link edges} are stored within PatientSpace and need to be converted to ProjectionSpace for intersections.
     * @returns An {@link Array} of {@link Vertex Vertices} that present all the intersection points.
     */
    public static intersections(
        edges: Edge[],
        toProjection: ReadonlyMat4
    ): Vertex[] {
        const intersections = new Array<Vertex>();
        let intersection: Vertex | undefined;
        for (const edge of edges) {
            if (
                (intersection = edge.intersection(
                    toProjection,
                    edges.length === 4
                ))
            ) {
                intersections.push(intersection);
            }
        }
        return intersections;
    }
}
